import { LinkIcon } from '@heroicons/react/20/solid';
import { useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { CountryFlag } from 'src/features/product/product-detail/ui/quotes-tab/CountryFlag';
import { FilesTab } from 'src/shared/components';
import { CTA_REORDER, CTA_SUPPORT } from 'src/shared/constants/update';
import { BulkOrdering } from 'src/shared/types/bulkOrdering';

import { Chip, cn, Divider, Spinner } from 'src/shared/components/ui';
import {
  ButtonV2,
  Modal,
  ModalBody,
  ModalContent,
  Tab,
  TabsV2,
  Typography
} from 'src/shared/components/ui';

import useProductUpdateInputModal from '../../product-detail/hooks/useProductUpdateInputModal';
import OrderDetailsModalTab from './OrderDetailsModalTab';
import OrderModalTimeline from './OrderModalTimeline';
import OrderShipmentsTab from './OrderShipmentsTab';

interface OrderDetailModalProps {
  order: BulkOrdering | null;
  isOpen: boolean;
  onClose: () => void;
  isLoading?: boolean;
  onRemoveSearchParams: () => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  isOpen,
  onClose,
  isLoading = false,
  onRemoveSearchParams,
  order
}) => {
  const { setData: setUpdateInputData } = useProductUpdateInputModal();

  const [selectedTab, setSelectedTab] = useState('timeline');

  const hasFiles = useMemo(() => {
    return (order?.customer_files?.length ?? 0) > 0;
  }, [order?.customer_files]);

  const withDetails = useMemo(() => {
    return order?.details || order?.sku_spec;
  }, [order?.details, order?.sku_spec]);

  const hasTimeline = useMemo(() => {
    return (order?.timeline?.length ?? 0) > 0;
  }, [order?.timeline]);

  const hasShipments = useMemo(() => {
    return (order?.user_shipments?.length ?? 0) > 0;
  }, [order?.user_shipments]);

  const showTabs = hasTimeline || withDetails || hasFiles || hasShipments;

  // Set default tab based on what's available
  useEffect(() => {
    if (hasTimeline) {
      setSelectedTab('timeline');
    } else if (withDetails) {
      setSelectedTab('details');
    } else if (hasFiles) {
      setSelectedTab('files');
    }
  }, [hasTimeline, withDetails, hasFiles]);

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'timeline':
        return <OrderModalTimeline order={order} isOpen={isOpen} />;
      case 'details':
        return <OrderDetailsModalTab order={order} />;
      case 'files':
        return <FilesTab files={order?.customer_files ?? null} />;
      case 'shipments':
        return <OrderShipmentsTab shipments={order?.user_shipments ?? []} />;
      default:
        return '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
          setTimeout(() => {
            onRemoveSearchParams();
          }, 300);
        }
      }}
      size="3xl"
      className={cn('h-[90%] max-w-[788px]', !showTabs ? 'h-auto' : '')}
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalBody className="p-0 flex flex-col gap-6 pb-5 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner />
                </div>
              ) : order ? (
                <>
                  <div className="flex justify-between items-start p-5 pb-2">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center">
                        <Typography
                          variant="h7"
                          weight="bold"
                          className="text-secondary-text"
                        >
                          Order
                        </Typography>
                        <div className="size-5 flex items-center justify-center">
                          <div className="size-1 bg-gray-4 rounded-full" />
                        </div>
                        <Typography
                          variant="h7"
                          weight="bold"
                          className="text-secondary-text"
                        >
                          {format(order?.requested_at, 'MMM dd, yyyy')}
                        </Typography>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Typography
                          variant="h2"
                          weight="bold"
                          className="mobile:text-2xl"
                        >
                          {order?.name}
                        </Typography>
                        <Chip color="default" className="bg-gray-2 rounded-lg">
                          <Typography
                            variant="body-3"
                            weight="medium"
                            className="text-secondary-text"
                          >
                            Type: {order?.product_quote?.product_name}
                          </Typography>
                        </Chip>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-row gap-5 items-center px-5 mobile:flex-col mobile:gap-3 mobile:py-0 mobile:items-stretch">
                    <div className="flex flex-col items-center justify-center flex-1 mobile:items-start mobile:flex-none">
                      <Typography
                        variant="body-1"
                        as="p"
                        className="text-secondary-text"
                        weight="medium"
                      >
                        Quantity
                      </Typography>
                      <Typography variant="h5" as="span" weight="bold">
                        {order?.order_quantity ?? 0}
                      </Typography>
                    </div>
                    <Divider orientation="vertical" className="mobile:hidden" />
                    <div className="flex flex-col items-center justify-center flex-1 mobile:items-start mobile:flex-none">
                      <Typography
                        variant="body-1"
                        as="p"
                        className="text-secondary-text"
                        weight="medium"
                      >
                        Supplier
                      </Typography>
                      {order?.cloak ? (
                        <div className="flex items-center gap-1">
                          <Typography variant="h5" as="span" weight="bold">
                            {order.cloak}
                          </Typography>
                          {order?.supplier_factory_country && (
                            <CountryFlag
                              countryCode={order.supplier_factory_country}
                            />
                          )}
                        </div>
                      ) : (
                        <Typography variant="h5" as="span" weight="bold">
                          None
                        </Typography>
                      )}
                    </div>
                    <Divider orientation="vertical" className="mobile:hidden" />
                    <div className="flex flex-col items-center justify-center flex-1 mobile:items-start mobile:flex-none">
                      <Typography
                        variant="body-1"
                        as="p"
                        className="text-secondary-text"
                        weight="medium"
                      >
                        Shipments
                      </Typography>
                      {order?.user_shipments?.length > 0 ? (
                        <div className="flex flex-col items-center gap-1 mobile:items-start">
                          {order.user_shipments.map((shipment) => (
                            <div
                              key={shipment.id}
                              onClick={() => {
                                if (shipment.tracking_url) {
                                  window.open(shipment.tracking_url, '_blank');
                                }
                              }}
                              className={
                                shipment.tracking_url
                                  ? 'flex items-center gap-1 cursor-pointer hover:opacity-50 transition-opacity'
                                  : 'flex items-center gap-1'
                              }
                            >
                              <LinkIcon className="size-4 pointer-events-none text-primary" />
                              <Typography className="text-primary text-sm">
                                {shipment.tracking_number || 'Tracking link'}
                              </Typography>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Typography variant="h5" as="span" weight="bold">
                          None
                        </Typography>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-start gap-2 px-5">
                    <ButtonV2
                      color="primary"
                      onPress={() => {
                        const data = {
                          name: order?.product?.name,
                          quantity: order?.order_quantity ?? 1
                        };

                        setUpdateInputData(data, CTA_REORDER);
                      }}
                    >
                      Reorder
                    </ButtonV2>

                    <ButtonV2
                      onPress={() => {
                        const data = {
                          name: order?.name
                        };

                        setUpdateInputData(data, CTA_SUPPORT);
                      }}
                      color="secondary"
                    >
                      Support
                    </ButtonV2>
                  </div>

                  {showTabs && (
                    <>
                      <div>
                        <div className="px-3">
                          <TabsV2
                            selectedKey={selectedTab}
                            onSelectionChange={(key) =>
                              setSelectedTab(key as string)
                            }
                          >
                            {hasTimeline && (
                              <Tab key="timeline" title="Timeline" />
                            )}
                            {withDetails && (
                              <Tab key="details" title="Order details" />
                            )}
                            {hasShipments && (
                              <Tab key="shipments" title="Shipments" />
                            )}
                            {hasFiles && <Tab key="files" title="Files" />}
                          </TabsV2>
                        </div>
                        <Divider className="w-full h-[1px] m-0 relative top-[-4px]" />
                      </div>

                      <div>{renderTabContent()}</div>
                    </>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <Typography variant="body-1" className="text-default-600">
                    Order not found
                  </Typography>
                </div>
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default OrderDetailModal;
